// The report page around a board with definition panels (Talk BI spec §7.3, Tasks 4.4a-b): the dark app bar,
// the page tabs, the filter pane on the inline-end side, each visual's frame, its states and the filter chips.
// The look is the approved comp's (docs/DESIGN.md, "Report surfaces"); every value shown is the server's or the
// viewer's own choice. The pure parts live in bi-report.mjs.
import {KpiTile, PivotTable, FlatTable, pivotize} from './bi-tables.jsx';
import {dimLabels} from './bi-tables.mjs';
import {toNumber} from './bi-charts.mjs';
import {renderResultChart} from './chat-render.jsx';
import {validateChartSpec} from './chat-blocks.mjs';
import {QUICK, monthSpan, yearOf, withYear, withMonths, quickPeriod, quickOf, periodLabel, monthName} from './data-dashboards.mjs';
import {fieldTitle, nameOf, labelOf, tableOf, emptyText, pickOf, pickedValue, compareMap, shownOf} from './bi-report.mjs';

const ICONS = {
  chart: 'M3 3v18h18M18 17V9M13 17V5M8 17v-3',
  pivot: 'M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM3 9h18M3 15h18M9 3v18M15 3v18',
  table: 'M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM3 9h18M3 15h18M9 3v18',
  max: 'M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7',
  min: 'M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7',
  filter: 'M22 3H2l8 9.46V19l4 2v-8.54z',
  reset: 'M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5',
  chevron: 'm15 18-6-6 6-6',
  db: 'M4 5c0-1.66 3.58-3 8-3s8 1.34 8 3-3.58 3-8 3-8-1.34-8-3zM4 5v14c0 1.66 3.58 3 8 3s8-1.34 8-3V5M4 12c0 1.66 3.58 3 8 3s8-1.34 8-3',
  rerun: 'M21 12a9 9 0 1 1-3-6.7L21 8M21 3v5h-5',
  download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3',
  x: 'M18 6 6 18M6 6l12 12',
  empty: 'M22 12h-6l-2 3h-4l-2-3H2M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z',
  unavailable: 'M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4',
  refused: 'M12 14l4-4M3.34 19a10 10 0 1 1 17.32 0',
  failed: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 8v4M12 16h.01',
  // the builder's (Task 5.1): the field kinds, the visual types, a drag grip and the doctor's notes
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  calendar: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  sigma: 'M18 7V4H6l6 8-6 8h12v-3',
  grip: 'M9 5h.01M9 12h.01M9 19h.01M15 5h.01M15 12h.01M15 19h.01',
  bars: 'M3 3v18h18M7 17v-5M11 17V7M15 17v-7M19 17V9',
  stacked: 'M3 3v18h18M7 17V8h4v9zM7 13h4M14 17v-6h4v6zM14 14h4',
  line: 'M3 3v18h18M7 15l4-5 3 3 6-7',
  donut: 'M21.21 15.89A10 10 0 1 1 8 2.83M22 12A10 10 0 0 0 12 2v10z',
  doctor: 'M11 2v2M5 2v2M5 3H4a2 2 0 0 0-2 2v4a6 6 0 0 0 12 0V5a2 2 0 0 0-2-2h-1M8 15a6 6 0 0 0 12 0v-3M22 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0z',
};
export const Icon = ({name, className = 'bi-icon'}) => <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d={ICONS[name]}/></svg>;

// The reply's rows as the flat table (tableOf), with a count when the server cut them (a pivot says its own).
function ResultTable({definition, result, dataset, lang}) {
  const t = tableOf(definition, result, dataset, lang);
  const text = t.keep.map((i, k) => (/^m\d+$/.test(result.columns[i]) ? null : dimLabels(t.rows.map(r => r[k]), result.columns[i], lang)));
  const columns = t.titles.map((label, k) => ({key: String(k), label, measure: !text[k]}));
  const objects = t.rows.map((r, j) => Object.fromEntries(r.map((v, k) => [String(k), text[k] ? text[k][j] : v])));
  return <>
    <FlatTable columns={columns} rows={objects} sortable lang={lang}/>
    {result.truncated && <p className="bi-foot">{shownOf(t.rows.length, result.groups, lang)}</p>}
  </>;
}

const STATE_ICONS = ['empty', 'unavailable', 'refused', 'failed'];
// A panel's state in place of its visual: empty, unavailable, refused or failed. note: the server's own words
// (English text), shown as a secondary line. Every state but empty is an error the reader is told of.
export function ReportState({state, text, note, lang}) {
  const he = lang === 'he';
  return <div className="bi-state" data-state={state} role={state === 'empty' ? undefined : 'status'}>
    <Icon name={STATE_ICONS.includes(state) ? state : 'failed'} className="bi-icon bi-state-icon"/>
    <p className="bi-state-text">{text}</p>
    {note && <p className="bi-state-note">{he ? 'תשובת השרת: ' : 'Server reply: '}<bdi>{note}</bdi></p>}
  </div>;
}

// A chart panel's rows -> the chat chart spec, so the prescription draws through chat's own mapping.
function chartSpec(definition, result, prescription, title, dataset, lang) {
  const {rows} = result;
  let labels, series;
  if (definition.series) {
    // a time grain on either axis reads as a date (the server sends date_trunc stamps), matched by its raw value
    const xs = [...new Set(rows.map(r => labelOf(r[0])))], ss = [...new Set(rows.map(r => labelOf(r[1])))];
    const shown = (vs, dim) => dimLabels(vs.map(v => (v === '—' ? null : v)), dim, lang);
    labels = shown(xs, definition.x);
    const names = shown(ss, definition.series);
    series = ss.map((s, j) => ({name: names[j], values: xs.map(x => {
      const row = rows.find(r => labelOf(r[0]) === x && labelOf(r[1]) === s);
      return row ? toNumber(row[2]) : null;
    })}));
  } else {
    labels = rows.map(r => labelOf(r[0]));
    series = definition.y.map((m, i) => ({name: fieldTitle(dataset, nameOf(m), lang), values: rows.map(r => toNumber(r[1 + i]))}));
  }
  const type = {donut: 'doughnut', line: 'line'}[prescription.mark] || 'bar';
  const checked = validateChartSpec({type, title, labels, series});
  return checked.ok ? {...checked.spec, prescription} : null;
}

// One definition panel's reply {result, prescription} drawn with the Part 3 components. onPick(choice, value)
// sets a cross-filter choice (spec §5.2): a donut slice or legend row, or a pivot row, on the choice's own dimension.
// onOrder(dim, keys) returns the panel's kept entity order, so a donut's or stack's colours follow the entity.
export function ReportVisual({definition, report, title, dataset, params, today, table, height, onPick, onOrder, lang}) {
  const {result, prescription} = report;
  const he = lang === 'he';
  const kpi = prescription.mark === 'kpi';
  const empty = !!result.empty_reason || !result.rows.length;
  const nothing = empty && emptyText(result, result.date_field && fieldTitle(dataset, result.date_field, lang), lang);
  if (empty && !kpi) return <ReportState state="empty" text={nothing} note={result.empty_reason} lang={lang}/>;
  if (table) return <ResultTable definition={definition} result={result} dataset={dataset} lang={lang}/>;
  if (kpi) {
    const at = result.columns.indexOf('m0');
    const compared = params.compare || !!definition.compare;
    return <KpiTile label="" value={result.totals?.m0 ?? result.rows[0]?.[at]} compareValue={result.compare_totals?.m0 ?? null}
      compared={compared} emptyReason={nothing || null} lang={lang}/>;
  }
  const y = yearOf(params.period, today);
  const pick = onPick ? pickOf(definition, params) : null;
  const choose = pick && (v => onPick(pick.name, pickedValue(v)));
  if (prescription.mark === 'pivot') {
    const cells = pivotize(result);
    const compare = result.compare_rows
      ? {label: y ? String(y - 1) : he ? 'אשתקד' : 'Last year', cells: pivotize({...result, rows: result.compare_rows})} : null;
    const selected = pick && pick.value !== null ? cells.rowKeys.find(rk => String(rk[pick.at]) === pick.value) ?? null : null;
    return <PivotTable rows={definition.rows.map(r => fieldTitle(dataset, r, lang))} cols={definition.cols ? fieldTitle(dataset, definition.cols, lang) : null}
      cells={cells} totals={definition.totals} heat={definition.heat} compare={compare} lang={lang}
      onRowPick={choose ? rk => choose(rk === null ? null : rk[pick.at]) : undefined} selected={selected}/>;
  }
  const spec = prescription.mark !== 'table' && chartSpec(definition, result, prescription, title, dataset, lang);
  if (!spec) return <ResultTable definition={definition} result={result} dataset={dataset} lang={lang}/>;
  const keys = {donut: spec.labels, stacked: spec.series.map(s => s.name)}[prescription.mark];
  const extra = {
    order: keys && onOrder ? onOrder(JSON.stringify([definition.x, definition.series ?? null]), keys) : undefined,
    ...(prescription.mark === 'donut' && choose
      ? {selected: pick.value, onPick: key => choose(key === null ? null : result.rows.find(r => labelOf(r[0]) === key)?.[0])} : {}),
    ...(prescription.mark === 'line' && result.compare_rows && !definition.series
      ? {compare: compareMap(result.compare_rows), names: y ? [String(y), String(y - 1)] : undefined} : {}),
  };
  // a donut keeps the comp's 180 px ring so its legend stays inside the card
  return renderResultChart(spec, lang, prescription.mark === 'donut' ? Math.min(height, 180) : height, extra);
}

// A visual's header tools (spec §7.3): table view, focus mode that Esc leaves, and the rows as CSV where a table
// is shown. onCsv: a function, null while there are no rows to give (disabled), undefined where no table is shown.
export function VisualTools({title, table, onTable, focused, onFocus, onCsv, lang}) {
  const he = lang === 'he';
  return <div className="bi-visual-tools">
    {onTable && <button type="button" className="bi-tool" aria-pressed={table} onClick={onTable}
      aria-label={`${he ? 'תצוגת טבלה' : 'Table view'}: ${title}`} title={he ? 'תצוגת טבלה' : 'Table view'}><Icon name="table"/></button>}
    <button type="button" className="bi-tool" data-focus-tool aria-pressed={focused} onClick={onFocus} aria-keyshortcuts={focused ? 'Escape' : undefined}
      aria-label={`${he ? 'מצב מיקוד' : 'Focus mode'}: ${title}`} title={focused ? he ? 'יציאה ממצב מיקוד (Esc)' : 'Leave focus mode (Esc)' : he ? 'מצב מיקוד' : 'Focus mode'}>
      <Icon name={focused ? 'min' : 'max'}/></button>
    {onCsv !== undefined && <button type="button" className="bi-tool" disabled={!onCsv} onClick={onCsv || undefined}
      aria-label={`${he ? 'הורדת CSV' : 'Download CSV'}: ${title}`} title={he ? 'הורדת השורות כקובץ CSV' : 'Download the rows as CSV'}><Icon name="download"/></button>}
  </div>;
}

// The page's active filters under its title, as in the comp: the period, the compare and each choice set
// (clicked or picked in the pane), which a chip's button clears.
export function ReportChips({period, compare, chips, unsaved, disabled, onRemove, lang}) {
  const he = lang === 'he';
  return <div className="bi-chips" aria-live="polite">
    <span className="bi-chip">{period}</span>
    {compare && <span className="bi-chip">{compare}</span>}
    {chips.map(c => <span className="bi-chip" data-active="true" key={c.name}><Icon name="filter"/><bdi>{c.label}</bdi>
      <button type="button" className="bi-chip-x" disabled={disabled} onClick={() => onRemove(c.name)}
        aria-label={he ? `הסרת הסינון ${c.label}` : `Remove the filter ${c.label}`} title={he ? 'הסרת הסינון' : 'Remove the filter'}><Icon name="x"/></button></span>)}
    {unsaved && <span className="bi-chips-note">{he ? 'מסננים שלא נשמרו' : 'Unsaved filters'}</span>}
  </div>;
}

export function ReportAppBar({title, env, readAt, lang}) {
  const he = lang === 'he';
  const read = readAt && new Intl.DateTimeFormat(he ? 'he-IL' : 'en-GB', {dateStyle: 'short', timeStyle: 'short'}).format(readAt);
  return <header className="bi-appbar">
    <p className="bi-crumbs"><Icon name="chart"/><span className="bi-crumb">{he ? 'שיחה עם הנתונים' : 'Talk to your data'}</span>
      <span className="bi-crumb" aria-hidden="true">/</span><span className="bi-crumb">{he ? 'לוחות נתונים' : 'Dashboards'}</span>
      <span className="bi-crumb" aria-hidden="true">/</span><b dir="auto">{title}</b></p>
    <p className="bi-appbar-meta">
      <span className="bi-meta"><Icon name="db"/><bdi>{env}</bdi>{read && <> · {he ? 'נקרא' : 'Read'} <bdi>{read}</bdi></>}</span>
      <span className="bi-meta"><Icon name="rerun"/>{he ? 'רץ מחדש בלי הסוכן' : 'Re-runs without the agent'}</span>
    </p>
  </header>;
}

// The board and the builder (Task 5.1): two tabs, one tab stop, arrows and Home/End move between them.
const TABS = [['board', 'tab', 'panel', 'chart', 'הדוח', 'Report'], ['builder', 'builder-tab', 'builder', 'pivot', 'בונה דוחות', 'Report builder']];
export function ReportTabs({id, tab, onTab, lang}) {
  const he = lang === 'he';
  const keys = e => {
    const at = TABS.findIndex(t => t[0] === tab);
    const to = {ArrowRight: at + (he ? -1 : 1), ArrowLeft: at + (he ? 1 : -1), Home: 0, End: TABS.length - 1}[e.key];
    if (to === undefined) return;
    e.preventDefault();
    const next = TABS[(to + TABS.length) % TABS.length];
    onTab(next[0]);
    document.getElementById(`${id}-${next[1]}`)?.focus();
  };
  return <div className="bi-tabs" role="tablist" aria-label={he ? 'עמודי הדוח' : 'Report pages'} onKeyDown={keys}>
    {TABS.map(([key, tid, pid, icon, hebrew, english]) => <button type="button" key={key} className="bi-tab" role="tab" id={`${id}-${tid}`}
      aria-selected={tab === key} aria-controls={`${id}-${pid}`} tabIndex={tab === key ? 0 : -1} onClick={() => onTab(key)}>
      <Icon name={icon}/>{he ? hebrew : english}</button>)}
  </div>;
}

function Card({title, value, children}) {
  return <div className="bi-fcard" role="group" aria-label={title}>
    <p className="bi-fcard-head"><span>{title}</span>{value && <span className="bi-fval" dir="auto">{value}</span>}</p>
    {children}
  </div>;
}

// The filter pane. params: the viewer's effective ReportParams; every control hands onChange the next params.
export function FilterPane({id, open, onToggle, params, dirty, disabled, today, years, dateTitles, choices, onChange, onReset, onSave, lang}) {
  const he = lang === 'he';
  const set = patch => onChange({...params, ...patch});
  const span = monthSpan(params.period, today);
  const year = yearOf(params.period, today);
  const quick = quickOf(params.period, today);
  const quickWords = {year: he ? 'כל השנה' : 'Whole year', ytd: he ? 'מתחילת השנה' : 'Year to date', this_quarter: he ? 'רבעון נוכחי' : 'This quarter',
    last_12_months: he ? '12 חודשים אחרונים' : 'Last 12 months'};
  const quickWord = key => quickWords[key] || (he ? `רבעון ${key.slice(1)}` : `Q${key.slice(1)}`);
  const months = Array.from({length: 12}, (_, i) => i + 1);
  const monthSelect = (label, which) => <label className="bi-field">{label}
    <select className="input" value={span ? span[which].m : ''} disabled={disabled}
      onChange={e => {const m = Number(e.target.value); set({period: withMonths(params.period, which === 'from' ? m : span?.from.m ?? 1, which === 'to' ? m : span?.to.m ?? 12, today)});}}>
      {!span && <option value="" disabled>—</option>}
      {months.map(m => <option key={m} value={m}>{monthName(m, lang, 'long')}</option>)}
    </select></label>;
  return <aside className="bi-pane" data-open={open} aria-label={he ? 'מסננים' : 'Filters'}>
    <div className="bi-pane-head">
      <Icon name="filter"/>
      <h3>{he ? 'מסננים' : 'Filters'}</h3>
      <button type="button" className="bi-link" disabled={disabled || !dirty} onClick={onReset} title={he ? 'חזרה למסננים השמורים בלוח' : 'Back to the filters saved on the board'}>
        <Icon name="reset"/>{he ? 'איפוס' : 'Reset'}</button>
      <button type="button" className="bi-tool bi-pane-toggle" aria-expanded={open} aria-controls={`${id}-filters`} onClick={onToggle}
        aria-label={open ? he ? 'כיווץ חלונית המסננים' : 'Collapse the filter pane' : he ? 'פתיחת חלונית המסננים' : 'Open the filter pane'}><Icon name="chevron"/></button>
    </div>
    <div className="bi-pane-body" id={`${id}-filters`} hidden={!open}>
      <Card title={he ? 'שנה' : 'Year'} value={year ? String(year) : null}>
        <div className="bi-seg">
          {years.map(y => <button type="button" key={y} aria-pressed={y === year} disabled={disabled} onClick={() => set({period: withYear(params.period, y, today)})}>{y}</button>)}
        </div>
      </Card>
      <Card title={he ? 'תקופה' : 'Period'} value={periodLabel(params.period, today, lang)}>
        {dateTitles.length > 0 && <p className="bi-fnote" dir="auto">{he ? 'לפי' : 'By'} {dateTitles.join(' · ')}</p>}
        <div className="bi-two">{monthSelect(he ? 'מחודש' : 'From month', 'from')}{monthSelect(he ? 'עד חודש' : 'To month', 'to')}</div>
        <div className="bi-presets" role="group" aria-label={he ? 'טווחים מהירים' : 'Quick ranges'}>
          {QUICK.map(key => <button type="button" key={key} aria-pressed={quick === key} disabled={disabled}
            onClick={() => set({period: quickPeriod(key, params.period, today)})}>{quickWord(key)}</button>)}
        </div>
      </Card>
      {choices.map(c => <Card key={c.name} title={c.title} value={params.choices[c.name].value}>
        <select className="input" aria-label={c.title} value={params.choices[c.name].value ?? ''} disabled={disabled}
          onChange={e => set({choices: {...params.choices, [c.name]: {...params.choices[c.name], value: e.target.value || null}}})}>
          <option value="">{he ? 'הכל' : 'All'}</option>
          {c.values.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        {c.state === 'loading' && <p className="bi-fnote" role="status">{he ? 'טוען ערכים…' : 'Loading values…'}</p>}
        {c.state === 'error' && <p className="bi-fnote">{he ? 'לא ניתן לטעון את הערכים.' : 'Could not load the values.'}</p>}
      </Card>)}
      <div className="bi-fcard">
        <label className="bi-switch"><span>{he ? 'השוואה לאותה תקופה אשתקד' : 'Compare with the same period last year'}</span>
          <input type="checkbox" role="switch" checked={params.compare} disabled={disabled} onChange={e => set({compare: e.target.checked})}/></label>
      </div>
      <div className="bi-pane-save">
        <button type="button" className="btn primary" disabled={disabled || !dirty} onClick={onSave}>{he ? 'שמירת המסננים בלוח' : 'Save filters to the board'}</button>
        <p className="bi-fnote">{dirty ? he ? 'הדף מציג מסננים שלא נשמרו.' : 'The page shows filters that are not saved.' : he ? 'המסננים שמורים בלוח.' : 'These filters are saved on the board.'}</p>
      </div>
    </div>
  </aside>;
}
